import React, { useRef, useState } from 'react';
import { Upload, X, Image as ImageIcon, Video, Loader2, Play } from 'lucide-react';
import { storageService } from '../../core/services/firebase/storageService';

interface MediaUploadProps {
  label?: string;
  type: 'image' | 'video';
  path: string; // Caminho base no storage (ex: 'profiles/avatars')
  onUploadComplete: (url: string) => void;
  currentUrl?: string;
  className?: string;
}

export const MediaUpload: React.FC<MediaUploadProps> = ({
  label,
  type,
  path,
  onUploadComplete,
  currentUrl,
  className = ''
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(currentUrl || null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0); // Simulado por enquanto
  const [error, setError] = useState('');

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 1. Validar
    const validation = storageService.validateFile(file, type);
    if (!validation.valid) {
      setError(validation.error || 'Arquivo inválido');
      return;
    }

    setError('');
    setLoading(true);
    setProgress(10); // Iniciou

    try {
      // 2. Preview Local Imediato
      const objectUrl = URL.createObjectURL(file);
      setPreview(objectUrl);

      // 3. Upload com Compressão
      const fileName = `${Date.now()}_${file.name}`;
      const fullPath = `${path}/${fileName}`;

      setProgress(40); // Comprimindo...
      
      const downloadUrl = await storageService.uploadFile(file, {
        path: fullPath,
        compress: type === 'image', // Só comprime imagens
        maxSizeMB: 0.8, // Compriimr para aprox 800kb
      });

      setProgress(100);
      onUploadComplete(downloadUrl);
    } catch (err) {
      console.error('Upload Error:', err);
      setError('Erro ao enviar arquivo. Tente novamente.');
      setPreview(currentUrl || null); // Reverte preview
    } finally {
      setLoading(false);
      setProgress(0);
    }
  };

  const clearSelection = () => {
    setPreview(null);
    onUploadComplete('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className={`w-full ${className}`}>
      {label && <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>}

      {!preview ? (
        // Estado Vazio (Upload Button)
        <div 
          onClick={() => !loading && fileInputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              !loading && fileInputRef.current?.click();
            }
          }}
          role="button"
          tabIndex={0}
          aria-label={`Clique para enviar ${type === 'image' ? 'uma foto' : 'um vídeo'}`}
          className={`
            border-2 border-dashed border-gray-300 rounded-lg p-6 
            flex flex-col items-center justify-center text-center cursor-pointer 
            transition-colors hover:border-blue-500 hover:bg-blue-50 focus:border-blue-500 focus:bg-blue-50 outline-none
            ${loading ? 'opacity-50 cursor-wait' : ''}
          `}
        >
          <div className="bg-blue-100 p-3 rounded-full text-blue-600 mb-3">
            {type === 'image' ? <ImageIcon size={24} /> : <Video size={24} />}
          </div>
          <p className="text-sm font-medium text-gray-900">
            {loading ? 'Processando...' : `Clique para enviar ${type === 'image' ? 'uma foto' : 'um vídeo'}`}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {type === 'image' ? 'JPG, PNG, WebP (Max 10MB)' : 'MP4, MOV (Max 50MB)'}
          </p>
        </div>
      ) : (
        // Estado com Preview
        <div className="relative rounded-lg overflow-hidden border border-gray-200 bg-black group">
          {type === 'image' ? (
            <img src={preview} alt="Preview" className="w-full h-48 object-cover sm:h-64" />
          ) : (
            <div className="w-full h-48 sm:h-64 flex items-center justify-center bg-gray-900">
              <video src={preview} className="max-h-full max-w-full" controls={false} />
              <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                <Play className="text-white opacity-80" size={48} fill="currentColor" />
              </div>
            </div>
          )}

          {/* Loading Overlay */}
          {loading && (
            <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center text-white backdrop-blur-sm">
               <Loader2 className="animate-spin mb-2" size={32} />
               <span className="text-sm font-medium">Otimizando {type}...</span>
            </div>
          )}

          {/* Actions (Só mostra se não estiver carregando) */}
          {!loading && (
             <button 
               onClick={clearSelection}
               className="absolute top-2 right-2 p-1.5 bg-black/60 text-white rounded-full hover:bg-red-600 transition-colors opacity-0 group-hover:opacity-100"
               title="Remover"
             >
               <X size={16} />
             </button>
          )}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <p className="text-xs text-red-600 mt-2">{error}</p>
      )}

      {/* Hidden Input */}
      <input 
        ref={fileInputRef}
        type="file"
        accept={type === 'image' ? "image/*" : "video/*"}
        title={`Selecionar ${type === 'image' ? 'foto' : 'vídeo'}`}
        className="hidden"
        onChange={handleFileSelect}
        disabled={loading}
      />
    </div>
  );
};
